import { useEffect, useState } from "react";
import { Check, Circle, X } from "lucide-react";
import { Link } from "react-router-dom";
import { API, authHeaders } from "@/lib/api";
import { motion, AnimatePresence } from "framer-motion";

export default function OnboardingChecklist() {
  const [state, setState] = useState(null);
  const [visible, setVisible] = useState(true);

  const load = async () => {
    try {
      const res = await fetch(`${API}/user/onboarding`, { headers: authHeaders() });
      if (res.ok) setState(await res.json());
    } catch {
      /* ignore */
    }
  };

  useEffect(() => {
    load();
    const iv = setInterval(load, 30_000);
    return () => clearInterval(iv);
  }, []);

  const dismiss = async () => {
    setVisible(false);
    try {
      await fetch(`${API}/user/acknowledgments`, {
        method: "POST",
        headers: authHeaders({ "Content-Type": "application/json" }),
        body: JSON.stringify({ ack_key: "onboarding_v1_dismissed" }),
      });
    } catch {
      /* ignore */
    }
  };

  if (!state || state.dismissed || !visible) return null;
  const allDone = state.done === state.total;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: -8 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -8 }}
        className="hm-surface p-4 relative"
        data-testid="onboarding-checklist"
      >
        <div className="flex items-center gap-2 mb-2">
          <div className="mono text-[10px] uppercase tracking-widest text-hm-accent">
            Onboarding · {state.done}/{state.total}
          </div>
          <button
            onClick={dismiss}
            data-testid="onboarding-dismiss"
            className="ml-auto h-6 w-6 rounded-md flex items-center justify-center text-hm-fg-subtle hover:text-hm-fg hover:bg-hm-bg-1"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
        {allDone ? (
          <div className="text-sm text-hm-fg">You&apos;re all set — welcome aboard.</div>
        ) : (
          <ul className="space-y-1.5">
            {state.items.map((item) => (
              <li key={item.key} data-testid={`onboarding-item-${item.key}`}>
                <Link
                  to={item.href}
                  className="flex items-center gap-2 text-xs text-hm-fg-muted hover:text-hm-fg"
                >
                  {item.done ? (
                    <Check className="h-3.5 w-3.5 text-hm-accent" />
                  ) : (
                    <Circle className="h-3.5 w-3.5 text-hm-fg-subtle" />
                  )}
                  <span className={item.done ? "line-through text-hm-fg-subtle" : ""}>
                    {item.label}
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </motion.div>
    </AnimatePresence>
  );
}
